<template>
  <ul
    class="card-handler"
    :class="
      `animation-direction-${animationDirection}
      ${animateSoft ? 'animate-soft' : ''}`
    "
  >
    <transition name="move">
      <li
        v-for="{ data, index } in filteredItems"
        :key="index"
        :data-index="index"
        class="card-handler-child"
      >
        <slot v-bind:data="data" v-bind:index="index" />
      </li>
    </transition>
  </ul>
</template>

<script>
import { debounce } from 'throttle-debounce'
import typesetMathMlMixin from '../../mixins/typeset-mathml'

/**
 * Displays a list of items as cards, one at a time.
 * Navigation between cards is visualized by a slide animation.
 *
 * This component doesn't have any navigation interface by itself, has three functions for navigation:
 * displayItemByIndex(index, soft)
 * displayNextItem(soft)
 * displayPreviousItem(soft)
 *
 * @author Thomas Grinderslev <thomas@edulab.dk>
 */
export default {
  name: 'CardHandler',
  mixins: [typesetMathMlMixin],
  props: {
    /**
     * An array of items to display
     */
    items: {
      type: Array,
      required: true,
    },

    /**
     * The index of the item to render initially
     */
    initialIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      /**
       * Whether to animate soft when transitioning between cards.
       * If a user triggers the transition directly, the transition should never be soft, since a direct actions feels more natural.
       * If a user doesn't trigger the transition directly, the transition should be soft, to make it feel less abrupt.
       */
      animateSoft: false,

      /**
       * The direction in which the animated items will move
       */
      animationDirection: 'none',

      /**
       * The index of the currently displayed item
       */
      currentItemIndex: this.initialIndex,
    }
  },
  computed: {
    /**
     * Maps the given items to a new array where each item is placed in an object along with its index
     * @returns {{data: *, index: number}[]}
     */
    mappedItems() {
      return this.items.map((data, index) => {
        return { data, index }
      })
    },

    /**
     * Picks out the item to render based on this.currentItemIndex and returns it in an array
     * to make it easy to animate between items using Vues build in transition tools.
     * @returns {{data: *, index: number}[]}
     */
    filteredItems() {
      return [this.mappedItems[this.currentItemIndex]]
    },
  },
  methods: {
    /**
     * Displays the item with the given index
     * @param {number} index
     * @param {boolean=} soft Adds ease-in to the transition.
     * @public
     */
    displayItemByIndex(index, soft) {
      if (index === this.currentItemIndex) {
        return
      }

      this.animateSoft = soft || false

      this.animationDirection = index > this.currentItemIndex ? 'left' : 'right'
      this.currentItemIndex = index

      this.$emit('indexWillChange')
      this.emitIndexChangeDelayed(index)
    },

    /**
     * Displays the previous item.
     * If the current item is the first item of the array, the previous item will be the last item.
     * @param {boolean=} soft Adds ease-in to the transition.
     * @public
     */
    displayPreviousItem(soft) {
      this.displayItemByIndex(
        (this.items.length + this.currentItemIndex - 1) % this.items.length,
        soft
      )
    },

    /**
     * Displays the next item.
     * If the current item is the last item of the array, the next item will be the first item.
     * @param {boolean=} soft Adds ease-in to the transition.
     * @public
     */
    displayNextItem(soft) {
      this.displayItemByIndex(
        (this.currentItemIndex + 1) % this.items.length,
        soft
      )
    },

    /**
     * Emits an index change event when the transition between elements is over.
     *
     * Because of the debouncer, the event is only emitted after the last of
     * several calls to the method to avoid any funky race conditions.
     *
     * The delay is set a little higher than the CSS transition to
     * make sure it has time to finish before emitting the event.
     *
     * @param {number} index
     * @private
     */
    emitIndexChangeDelayed: debounce(501, function(index) {
      // Remove the soft tag to make sure any following animations aren't soft by accident
      this.animateSoft = false
      this.$emit('indexChanged', index)
    }),
  },
}
</script>

<style lang="scss" scoped>
.card-handler {
  position: relative;
  overflow: hidden;
}

.card-handler-child {
  position: relative;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  background: none;

  &.move-enter-active,
  &.move-leave-active {
    transition: all 0.5s ease-out;
  }

  &.move-leave-active {
    position: absolute;
    width: 100%;
  }

  &.move-enter,
  &.move-leave-to {
    opacity: 0;
  }
}

.card-handler.animate-soft .card-handler-child {
  &.move-enter-active,
  &.move-leave-active {
    transition-timing-function: ease-in-out;
  }
}

.animation-direction-left {
  .card-handler-child {
    &.move-enter {
      transform: translate(calc(100% + 4em), 0);
    }
    &.move-leave-to {
      transform: translate(calc(-100% - 4em), 0);
    }
  }
}

.animation-direction-right {
  .card-handler-child {
    &.move-enter {
      transform: translate(calc(-100% - 4em), 0);
    }
    &.move-leave-to {
      transform: translate(calc(100% + 4em), 0);
    }
  }
}
</style>
